import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  Checkbox,
  Chip,
  Collapse,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TextFieldCustom } from '../helpers/formFields';

// Define a mapping for category labels to color keys.
const categoryColors = {
  "Cost Savings": "success.main",
  "Extra Costs": "error.main",
  "Revenue Enhancements": "primary.main",
  "Risk Lowering": "warning.main"
};

const categoryLabels = {
  cost_savings: "Cost Savings",
  extra_costs: "Extra Costs",
  revenue_enhancements: "Revenue Enhancements",
  risk_lowering: "Risk Lowering"
};

// ImpactCard component: renders a single impact item card.
const ImpactCard = ({ id, category, item, checked, onCheckboxChange }) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded(prev => !prev);
  };

  return (
    <Card sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, p: 2 }}>
      {/* Checkbox at the top left */}
      <Checkbox
        checked={checked !== undefined ? checked : true}
        size='small'
        onChange={(e) => onCheckboxChange(id, e.target.checked)}
        sx={{ mt: 0.5, mr: 1 }}
      />
      <Box sx={{ flexGrow: 1 }}>
        {/* Title */}
        <Typography variant="body1">{item.title}</Typography>
        {/* Category badge */}
        <Chip
          label={category}
          size="small"
          sx={{
            backgroundColor: categoryColors[category] || 'grey',
            color: 'white',
            mt: 1,
            mb: 1,
            height: 20,
            fontSize: '0.65rem',
            px: 0.5,
            py: 0
          }}
        />
        {/* Explanation section */}
        <Box>
          <Button
            onClick={handleToggle}
            size="small"
            sx={{ textTransform: 'none', p: 0 }}
          >
            {expanded ? 'Hide Explanation' : 'Show Explanation'}{' '}
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Button>
          <Collapse in={expanded}>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {item.explanation}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </Card>
  );
};

// The categories (keys) of impactData.
const categories = ["cost_savings", "extra_costs", "revenue_enhancements", "risk_lowering"];

// Main component: ImpactAnalysisDisplay.
const ImpactAnalysisDisplay = ({
  impactData = { cost_savings: [], extra_costs: [], revenue_enhancements: [], risk_lowering: [] },
  onSubmitChecked,
  onSubmitInput
}) => {

  const [allItems, setAllItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [inputText, setInputText] = useState("");
  const [focused, setFocused] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Update flattened items and merge with existing checkedItems when impactData changes.
  useEffect(() => {
    const newAllItems = [];
    categories.forEach(category => {
      (impactData[category] || []).forEach((item, index) => {
        newAllItems.push({
          id: `${category}-${index}`,
          catKey: category, // store the original key for grouping later
          category: categoryLabels[category],
          item
        });
      });
    });
    setAllItems(newAllItems);

    setCheckedItems(prev => {
      const newChecked = { ...prev };
      // Add new keys if they do not exist.
      newAllItems.forEach(item => {
        if (!(item.id in newChecked)) {
          newChecked[item.id] = true;
        }
      });
      // Optionally, remove keys no longer present.
      // const filteredChecked = {};
      // newAllItems.forEach(item => { filteredChecked[item.id] = newChecked[item.id]; });
      // return filteredChecked;
      return newChecked;
    });
  }, [impactData]);

  // Checkbox change handler.
  const handleCheckboxChange = (id, value) => {
    setCheckedItems(prev => ({ ...prev, [id]: value }));
  };

  // Submit handler for the checkboxes.
  const handleSubmitChecked = () => {
    // Filter out selected items.
    const selectedItems = allItems.filter(item => checkedItems[item.id]);
    // Group the selected items by their original category key.
    const grouped = {};
    categories.forEach((catKey) => {
      grouped[catKey] = selectedItems
        .filter(item => item.catKey === catKey)
        .map(item => item.item);
    });
    onSubmitChecked(grouped);
  };

  // Handler for submitting the input text.
  const handleInputSubmit = () => {
    onSubmitInput(inputText);
    setInputText("");
  };

  return (
    <Paper
      sx={{
        p: 3,
        maxWidth: 800,
        m: 'auto',
        // bgcolor: "primary.verylight",
        background: theme =>
          `linear-gradient(
to bottom right,
${theme.palette.primary.main} 0%,
${theme.palette.primary.verylight} 100%
)`,
        borderRadius: '30px',
        mb: 5
      }}
    >
      {/* Heading */}
      <Typography variant="h5" gutterBottom sx={{ color: 'white' }}>
        Outcome Analysis
      </Typography>

      {/* Instruction Toggle */}
      <Box sx={{ mb: 3 }}>
        <Button
          onClick={() => setExpanded(prev => !prev)}
          size="small"
          sx={{ textTransform: 'none', p: 0, color: 'white' }}
        >
          {expanded ? 'Hide Instruction' : 'Show Instruction'}{' '}
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Button>
        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ mt: 1, color: 'white' }}>
            Based on our analysis, I've identified several areas where your product may impact your customers in the industry.
            Please review the list below and uncheck any areas that are not relevant. If you believe any important areas are missing,
            use the input field to request additional considerations. When you are ready, press 'Start' to proceed.
          </Typography>
        </Collapse>
      </Box>

      {/* List of Impact Cards */}
      {allItems.map(({ id, category, item }) => (
        <ImpactCard
          key={id}
          id={id}
          category={category}
          item={item}
          checked={checkedItems[id]}
          onCheckboxChange={handleCheckboxChange}
        />
      ))}

      {/* Input field with submit button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 3 }}>
        <TextFieldCustom
          label={!focused ? "Anything missing? Please write a sentence or two and I will try to add" : ""}
          name="impact_area"
          variant="outlined"
          fullWidth
          size="small"
          value={inputText || ""}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{
            '& .MuiInputBase-input': {
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '8px'
            }
          }}
        />
        <Button
          startIcon={<AutoFixHighIcon />}
          variant="contained"
          sx={{ ml: 2, textTransform: 'none', bgcolor: 'white', color: 'primary.main' }}
          onClick={handleInputSubmit}
        >
          Request
        </Button>
      </Box>

      {/* Submit button for checked items */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" onClick={handleSubmitChecked} sx={{ textTransform: 'none' }}>
          Start setting up Value Model
        </Button>
      </Box>
    </Paper>
  );
};

export default ImpactAnalysisDisplay;
